import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Package, TrendingDown, Clock, ChevronLeft, ChevronRight } from 'lucide-react';
import StockLotCard from './StockLotCard';
import { supabase } from '../../lib/supabase';

const StockLotSection: React.FC = () => {
  const [stockLots, setStockLots] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);

  const dummyStockLots: any[] = [
    {
      id: 'stock-1',
      title: 'Premium Cotton T-Shirts - Overstock',
      description: 'High-quality cotton t-shirts in various colors and sizes. Perfect for retail or bulk orders.',
      quantity: 500,
      original_price: 25.99,
      discounted_price: 15.99,
      discount_percentage: 38,
      images: ['https://images.pexels.com/photos/996329/pexels-photo-996329.jpeg'],
      category: 'Garments',
      condition: 'New',
      expiry_date: '2024-12-31T00:00:00Z',
      location: 'Dhaka Warehouse',
      supplier: {
        organization: 'Bengal Textiles Ltd.',
        location: 'Dhaka, Bangladesh',
        verified: true
      },
      created_at: '2024-01-15T00:00:00Z'
    },
    {
      id: 'stock-2',
      title: 'Handwoven Jute Bags - Clearance Sale',
      description: 'Eco-friendly jute shopping bags with reinforced handles. Sustainable and durable.',
      quantity: 1000,
      original_price: 12.99,
      discounted_price: 7.99,
      discount_percentage: 38,
      images: ['https://images.pexels.com/photos/5217911/pexels-photo-5217911.jpeg'],
      category: 'Jute Products',
      condition: 'New',
      expiry_date: '2024-11-30T00:00:00Z',
      location: 'Khulna Warehouse',
      supplier: {
        organization: 'Eco Jute Co.',
        location: 'Khulna, Bangladesh',
        verified: true
      },
      created_at: '2024-02-01T00:00:00Z'
    },
    {
      id: 'stock-3',
      title: 'Leather Wallets - End of Season',
      description: 'Premium leather wallets in brown and black. Genuine leather with multiple card slots.',
      quantity: 200,
      original_price: 45.99,
      discounted_price: 29.99,
      discount_percentage: 35,
      images: ['https://images.pexels.com/photos/1152077/pexels-photo-1152077.jpeg'],
      category: 'Leather Goods',
      condition: 'New',
      expiry_date: '2024-10-31T00:00:00Z',
      location: 'Dhaka Warehouse',
      supplier: {
        organization: 'Dhaka Leather Works',
        location: 'Dhaka, Bangladesh',
        verified: true
      },
      created_at: '2024-01-20T00:00:00Z'
    },
    {
      id: 'stock-4',
      title: 'Cotton Polo Shirts - Factory Surplus',
      description: 'Classic polo shirts in navy, white, and gray. Perfect for corporate uniforms or casual wear.',
      quantity: 300,
      original_price: 32.99,
      discounted_price: 19.99,
      discount_percentage: 39,
      images: ['https://images.pexels.com/photos/996329/pexels-photo-996329.jpeg'],
      category: 'Garments',
      condition: 'New',
      expiry_date: '2024-09-30T00:00:00Z',
      location: 'Gazipur Warehouse',
      supplier: {
        organization: 'Cotton Craft BD',
        location: 'Gazipur, Bangladesh',
        verified: true
      },
      created_at: '2024-02-10T00:00:00Z'
    },
    {
      id: 'stock-5',
      title: 'Traditional Jamdani Sarees - Limited Stock',
      description: 'Authentic handwoven Jamdani sarees with intricate patterns. Cultural heritage pieces.',
      quantity: 50,
      original_price: 120.00,
      discounted_price: 89.99,
      discount_percentage: 25,
      images: ['https://images.pexels.com/photos/7148621/pexels-photo-7148621.jpeg'],
      category: 'Traditional Wear',
      condition: 'New',
      expiry_date: '2024-08-31T00:00:00Z',
      location: 'Dhaka Warehouse',
      supplier: {
        organization: 'Heritage Weavers',
        location: 'Dhaka, Bangladesh',
        verified: true
      },
      created_at: '2024-01-25T00:00:00Z'
    },
    {
      id: 'stock-6',
      title: 'Frozen Hilsa Fish - Bulk Deal',
      description: 'Premium quality frozen Hilsa fish, individually wrapped. Fresh from Bay of Bengal.',
      quantity: 100,
      original_price: 35.99,
      discounted_price: 25.99,
      discount_percentage: 28,
      images: ['https://images.pexels.com/photos/1267320/pexels-photo-1267320.jpeg'],
      category: 'Frozen Foods',
      condition: 'New',
      expiry_date: '2024-07-31T00:00:00Z',
      location: 'Chittagong Cold Storage',
      supplier: {
        organization: 'Bengal Fisheries',
        location: 'Chittagong, Bangladesh',
        verified: true
      },
      created_at: '2024-02-05T00:00:00Z'
    }
  ];

  // Check if mobile view
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => {
      window.removeEventListener('resize', checkMobile);
    };
  }, []);

  const itemsPerSlide = isMobile ? 1 : 3;
  const totalSlides = Math.ceil((stockLots.length > 0 ? stockLots.length : dummyStockLots.length) / itemsPerSlide);

  useEffect(() => {
    const fetchStockLots = async () => {
      try {
        setLoading(true);
        
        // Try to fetch from database
        const { data, error } = await supabase
          .from('stock_lots')
          .select(`
            *,
            suppliers:supplier_id (
              organization,
              location,
              verified
            )
          `)
          .eq('status', 'active')
          .order('created_at', { ascending: false })
          .limit(6);
        
        if (error || !data || data.length === 0) {
          // Use dummy data if no real data
          setStockLots(dummyStockLots);
        } else {
          // Transform real data to match expected format
          const transformedData = data.map(item => ({
            ...item,
            supplier: item.suppliers
          }));
          // Combine real data with dummy data for demo
          setStockLots([...transformedData, ...dummyStockLots]);
        }
      } catch (err) {
        console.error('Error fetching stock lots:', err);
        setStockLots(dummyStockLots);
      } finally {
        setLoading(false);
      }
    };

    fetchStockLots();
  }, []);

  const nextSlide = () => {
    const totalItems = stockLots.length > 0 ? stockLots.length : dummyStockLots.length;
    const slides = Math.ceil(totalItems / itemsPerSlide);
    setCurrentSlide((prev) => (prev + 1) % slides);
  };

  const prevSlide = () => {
    const totalItems = stockLots.length > 0 ? stockLots.length : dummyStockLots.length;
    const slides = Math.ceil(totalItems / itemsPerSlide);
    setCurrentSlide((prev) => (prev - 1 + slides) % slides);
  };

  // Auto-slide every 8 seconds
  useEffect(() => {
    const totalItems = stockLots.length > 0 ? stockLots.length : dummyStockLots.length;
    if (totalItems > itemsPerSlide) {
      const interval = setInterval(nextSlide, 8000);
      return () => clearInterval(interval);
    }
  }, [stockLots.length, dummyStockLots.length, itemsPerSlide, isMobile]);

  // Touch handlers for mobile swiping
  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe && displayStockLots.length > itemsPerSlide) {
      nextSlide();
    }
    if (isRightSwipe && displayStockLots.length > itemsPerSlide) {
      prevSlide();
    }
  };

  // Use stockLots if available, otherwise use dummy data
  const displayStockLots = stockLots.length > 0 ? stockLots : dummyStockLots;
  const totalSlidesToShow = Math.ceil(displayStockLots.length / itemsPerSlide);

  if (loading) {
    return (
      <div className="w-full h-64 flex items-center justify-center bg-gray-100 rounded-lg">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  return (
    <section className="py-16 bg-orange-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex items-center justify-center mb-4">
            <Package className="h-8 w-8 text-orange-600 mr-3" />
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
              Stock Lot Deals
            </h2>
          </div>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Discover amazing deals on surplus inventory and overstock items from verified suppliers
          </p>
          <div className="flex items-center justify-center space-x-6 mt-4 text-sm text-gray-600">
            <div className="flex items-center">
              <TrendingDown className="h-4 w-4 text-green-600 mr-1" />
              <span>Up to 40% Off</span>
            </div>
            <div className="flex items-center">
              <Clock className="h-4 w-4 text-orange-600 mr-1" />
              <span>Limited Time</span>
            </div>
            <div className="flex items-center">
              <Package className="h-4 w-4 text-blue-600 mr-1" />
              <span>Bulk Quantities</span>
            </div>
          </div>
        </div>

        {/* Stock Lot Slider */}
        <div className="relative">
          {/* Navigation Buttons */}
          {displayStockLots.length > itemsPerSlide && (
            <>
              <button
                onClick={prevSlide}
                className="absolute left-4 top-1/2 transform -translate-y-1/2 z-10 bg-white/90 backdrop-blur-sm p-3 rounded-full shadow-lg hover:bg-white transition-all duration-200 hover:scale-110"
              >
                <ChevronLeft className="h-6 w-6 text-gray-700" />
              </button>
              <button
                onClick={nextSlide}
                className="absolute right-4 top-1/2 transform -translate-y-1/2 z-10 bg-white/90 backdrop-blur-sm p-3 rounded-full shadow-lg hover:bg-white transition-all duration-200 hover:scale-110"
              >
                <ChevronRight className="h-6 w-6 text-gray-700" />
              </button>
            </>
          )}

          {/* Slider Container */}
          <div className="overflow-hidden rounded-xl">
            <div 
              className="flex transition-transform duration-500 ease-in-out"
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
              style={{ 
                transform: `translateX(-${currentSlide * 100}%)`,
                width: `${totalSlidesToShow * 100}%`
              }}
            >
              {Array.from({ length: totalSlidesToShow }).map((_, slideIndex) => (
                <div 
                  key={slideIndex}
                  className={`flex ${isMobile ? 'space-x-0 px-4' : 'space-x-6 px-2'}`}
                  style={{ width: `${100 / totalSlidesToShow}%` }}
                >
                  {displayStockLots
                    .slice(slideIndex * itemsPerSlide, (slideIndex + 1) * itemsPerSlide)
                    .map((stockLot) => (
                      <div key={stockLot.id} className="flex-1">
                        <StockLotCard stockLot={stockLot} />
                      </div>
                    ))}
                </div>
              ))}
            </div>
          </div>

          {/* Slide Indicators */}
          {displayStockLots.length > itemsPerSlide && (
            <div className="flex justify-center mt-6 space-x-2">
              {Array.from({ length: totalSlidesToShow }).map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentSlide(index)}
                  className={`w-3 h-3 rounded-full transition-all duration-200 ${
                    currentSlide === index 
                      ? 'bg-orange-600 scale-125' 
                      : 'bg-gray-300 hover:bg-gray-400'
                  }`}
                />
              ))}
            </div>
          )}
        </div>

        {/* View All Button */}
        <div className="text-center mt-12">
          <Link 
            to="/stock-lots"
            className="inline-flex items-center space-x-2 bg-orange-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-orange-700 transition-colors shadow-md"
          >
            <span>View All Stock Lots</span>
            <ArrowRight className="h-5 w-5" />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default StockLotSection;